import { ApiError, apiFetch } from "./client";
import type { ArchivedWorkspacePage, WorkspaceBlock, WorkspaceBlockContent, WorkspaceBlockType, WorkspacePageDetail, WorkspacePageSummary } from "@/lib/workspace-pages";

export type { WorkspaceBlock, WorkspaceBlockContent, WorkspaceBlockType, WorkspacePageDetail, WorkspacePageSummary };

export class WorkspaceBlockConflictError extends Error {
  currentBlock: WorkspaceBlock | null;

  constructor(currentBlock: WorkspaceBlock | null = null) {
    super("Workspace block was modified elsewhere");
    this.name = "WorkspaceBlockConflictError";
    this.currentBlock = currentBlock;
  }
}

export class WorkspacePageConflictError extends Error {
  currentPage: WorkspacePageSummary | null;

  constructor(currentPage: WorkspacePageSummary | null = null) {
    super("Workspace page metadata was modified elsewhere");
    this.name = "WorkspacePageConflictError";
    this.currentPage = currentPage;
  }
}

export async function fetchWorkspacePages(): Promise<WorkspacePageSummary[]> {
  const data = await apiFetch<{ pages: WorkspacePageSummary[] }>("/api/workspace-pages");
  return data.pages;
}

export async function createWorkspacePage(
  title: string,
  options: { insertAfterId?: string; isDraft?: boolean; storeRef?: string } = {},
): Promise<WorkspacePageSummary> {
  return apiFetch<WorkspacePageSummary>("/api/workspace-pages", {
    method: "POST",
    body: JSON.stringify({
      title,
      ...(options.insertAfterId ? { insertAfterId: options.insertAfterId } : {}),
      ...(options.isDraft ? { isDraft: true } : {}),
      ...(options.storeRef ? { storeRef: options.storeRef } : {}),
    }),
  });
}

export async function fetchWorkspacePage(id: string): Promise<WorkspacePageDetail> {
  return apiFetch<WorkspacePageDetail>(`/api/workspace-pages/${encodeURIComponent(id)}`);
}

export async function updateWorkspacePage(id: string, patch: { title?: string; icon?: string | null; cover?: string | null; description?: string | null; isDraft?: boolean; expectedUpdatedAt?: string }): Promise<WorkspacePageSummary> {
  try {
    return await apiFetch<WorkspacePageSummary>(`/api/workspace-pages/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object"
      && (err.body as { code?: string }).code === "WORKSPACE_PAGE_CONFLICT") {
      throw new WorkspacePageConflictError((err.body as { currentPage?: WorkspacePageSummary }).currentPage ?? null);
    }
    throw err;
  }
}

export async function deleteWorkspacePage(id: string): Promise<void> {
  await apiFetch(`/api/workspace-pages/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function fetchArchivedWorkspacePages(): Promise<ArchivedWorkspacePage[]> {
  const data = await apiFetch<{ pages: ArchivedWorkspacePage[] }>("/api/workspace-pages/archived");
  return data.pages;
}

export function restoreWorkspacePage(id: string): Promise<WorkspacePageSummary> {
  return apiFetch<WorkspacePageSummary>("/api/workspace-pages/archived", { method: "PATCH", body: JSON.stringify({ id }) });
}

export async function createWorkspaceBlock(
  pageId: string,
  type: WorkspaceBlockType,
  content: WorkspaceBlockContent,
  links: Partial<Pick<WorkspaceBlock, "linked_store_id" | "linked_offer_id" | "listing_key" | "linked_view_id" | "linked_task_id">> = {},
): Promise<WorkspaceBlock> {
  return apiFetch<WorkspaceBlock>(`/api/workspace-pages/${encodeURIComponent(pageId)}/blocks`, {
    method: "POST",
    body: JSON.stringify({ type, content, ...links }),
  });
}

export async function reorderWorkspaceBlocks(pageId: string, ids: string[]): Promise<void> {
  await apiFetch(`/api/workspace-pages/${encodeURIComponent(pageId)}/blocks`, {
    method: "PATCH",
    body: JSON.stringify({ ids }),
  });
}

export async function updateWorkspaceBlock(
  id: string,
  patch: { type?: WorkspaceBlockType; content?: WorkspaceBlockContent; position?: number; expectedUpdatedAt?: string; sourceTemplateId?: string } & Partial<Pick<WorkspaceBlock, "linked_store_id" | "linked_offer_id" | "listing_key" | "linked_view_id" | "linked_task_id">>,
): Promise<WorkspaceBlock> {
  try {
    return await apiFetch<WorkspaceBlock>(`/api/workspace-blocks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object"
      && (err.body as { code?: string }).code === "WORKSPACE_BLOCK_CONFLICT") {
      throw new WorkspaceBlockConflictError((err.body as { currentBlock?: WorkspaceBlock }).currentBlock ?? null);
    }
    throw err;
  }
}

export async function deleteWorkspaceBlock(id: string): Promise<void> {
  await apiFetch(`/api/workspace-blocks/${encodeURIComponent(id)}`, { method: "DELETE" });
}
